(() => {
  // Shared image interaction for the PDP viewer and the Quick View viewer.
  // The host owns the dialog, image selection, focus, and announcements.
  window.ThemeMediaViewer = function ThemeMediaViewer({ viewport, image, getAdjacent, onNavigate, onUpgrade, onZoomChange, onTap }) {
    const doubleTapMs = 320;
    const tapMovement = 10;
    const doubleTapDistance = 32;
    const tapZoomDuration = 260;
    const pointers = new Map();
    let zoom = 1;
    let pan = { x: 0, y: 0 };
    let gesture = null;
    let slide = null;
    let suppressClick = false;
    let pendingTap = null;
    const touchClicks = [];
    let lastTouchTapTime = -Infinity;
    const preloads = new Map();
    const navigationQueue = [];
    let waitingForImage = false;
    let navigationGeneration = 0;
    let tapAnimation = null;

    const preload = (src) => {
      if (preloads.has(src)) return preloads.get(src);
      const resource = new window.Image();
      const entry = { state: 'loading', promise: null };
      entry.promise = new Promise((resolve) => {
        let decoding = false;
        const complete = (ready) => {
          if (entry.state !== 'loading') return;
          entry.state = ready ? 'ready' : 'error';
          resource.onload = null;
          resource.onerror = null;
          resolve(ready);
        };
        resource.onload = () => {
          if (decoding) return;
          decoding = true;
          if (!resource.naturalWidth) return complete(false);
          if (typeof resource.decode === 'function') {
            try {
              resource.decode().then(() => complete(true), () => complete(false));
            } catch (_) {
              complete(false);
            }
          } else {
            complete(true);
          }
        };
        resource.onerror = () => complete(false);
        resource.src = src;
        if (resource.complete && resource.naturalWidth) resource.onload();
      });
      preloads.set(src, entry);
      return entry;
    };

    const visualSource = (adjacent) => {
      if (preload(adjacent.src).state === 'ready') return adjacent.src;
      const previewSrc = adjacent.previewSrc || adjacent.src;
      return preload(previewSrc).state === 'ready' ? previewSrc : null;
    };

    const waitForVisual = (adjacent) => {
      const full = preload(adjacent.src);
      const preview = preload(adjacent.previewSrc || adjacent.src);
      const ready = visualSource(adjacent);
      if (ready || (full.state === 'error' && preview.state === 'error')) return Promise.resolve(ready);
      return new Promise((resolve) => {
        const check = () => {
          const source = visualSource(adjacent);
          if (source || (full.state === 'error' && preview.state === 'error')) resolve(source);
        };
        full.promise.then(check);
        if (preview !== full) preview.promise.then(check);
      });
    };

    const prime = () => {
      const adjacentSources = new Set();
      for (const direction of [-1, 1]) {
        const adjacent = getAdjacent?.(direction);
        if (adjacent?.src) {
          adjacentSources.add(adjacent.src);
          preload(adjacent.src);
          if (adjacent.previewSrc) {
            adjacentSources.add(adjacent.previewSrc);
            preload(adjacent.previewSrc);
          }
        }
      }
      for (const src of preloads.keys()) {
        if (!adjacentSources.has(src)) preloads.delete(src);
      }
    };

    const fittedSize = () => {
      const width = image.naturalWidth || image.width || 1;
      const height = image.naturalHeight || image.height || 1;
      const ratio = Math.min(viewport.clientWidth / width, viewport.clientHeight / height);
      return { width: width * ratio, height: height * ratio };
    };

    const render = () => {
      const fitted = fittedSize();
      const maxX = Math.max(0, (fitted.width * zoom - viewport.clientWidth) / 2);
      const maxY = Math.max(0, (fitted.height * zoom - viewport.clientHeight) / 2);
      pan.x = Math.max(-maxX, Math.min(maxX, pan.x));
      pan.y = Math.max(-maxY, Math.min(maxY, pan.y));
      // Lay out the source at its displayed size. Scaling a persistent layer
      // makes iOS Safari show a soft image until movement stops.
      image.style.width = `${fitted.width * zoom}px`;
      image.style.height = `${fitted.height * zoom}px`;
      image.style.left = `calc(50% + ${pan.x}px)`;
      image.style.top = `calc(50% + ${pan.y}px)`;
      viewport.classList.toggle('is-zoomed', zoom > 1);
      onZoomChange?.(zoom);
    };

    const cancelTapAnimation = () => {
      if (!tapAnimation) return;
      window.clearTimeout?.(tapAnimation.timer);
      tapAnimation = null;
      const rect = image.getBoundingClientRect?.();
      const fitted = fittedSize();
      image.style.transition = 'none';
      if (rect?.width > 0 && fitted.width > 0) {
        const viewportRect = viewport.getBoundingClientRect();
        zoom = Math.max(1, Math.min(4, rect.width / fitted.width));
        pan = {
          x: rect.left + rect.width / 2 - viewportRect.left - viewport.clientWidth / 2,
          y: rect.top + rect.height / 2 - viewportRect.top - viewport.clientHeight / 2
        };
      }
      render();
    };

    const setZoom = (value) => {
      cancelTapAnimation();
      zoom = Math.max(1, Math.min(4, value));
      if (zoom === 1) pan = { x: 0, y: 0 };
      render();
    };

    const clearPendingTap = () => {
      if (pendingTap?.timer != null) window.clearTimeout?.(pendingTap.timer);
      pendingTap = null;
    };

    const clearTouchClicks = () => {
      for (const click of touchClicks) window.clearTimeout?.(click.timer);
      touchClicks.length = 0;
    };

    const zoomAt = (value, clientPoint) => {
      const nextZoom = Math.max(1, Math.min(4, value));
      if (nextZoom === 1) return setZoom(1);
      const rect = viewport.getBoundingClientRect();
      const x = clientPoint.x - rect.left - viewport.clientWidth / 2;
      const y = clientPoint.y - rect.top - viewport.clientHeight / 2;
      const ratio = nextZoom / zoom;
      pan = { x: x - (x - pan.x) * ratio, y: y - (y - pan.y) * ratio };
      zoom = nextZoom;
      render();
    };

    const animateDoubleTap = (clientPoint, nextZoom) => {
      cancelTapAnimation();
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        zoomAt(nextZoom, clientPoint);
        return;
      }
      image.style.transition = 'none';
      void image.offsetWidth; // Establish the current size before transitioning.
      image.style.transition = `width ${tapZoomDuration}ms cubic-bezier(0.22, 1, 0.36, 1), height ${tapZoomDuration}ms cubic-bezier(0.22, 1, 0.36, 1), left ${tapZoomDuration}ms cubic-bezier(0.22, 1, 0.36, 1), top ${tapZoomDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
      zoomAt(nextZoom, clientPoint);
      const animation = { targetZoom: nextZoom, timer: null };
      animation.timer = window.setTimeout(() => {
        if (tapAnimation !== animation) return;
        tapAnimation = null;
        image.style.transition = '';
      }, tapZoomDuration);
      tapAnimation = animation;
    };

    const recordTap = (clientPoint, time, toggleFromZoom) => {
      if (pendingTap && time - pendingTap.time <= doubleTapMs
        && Math.hypot(clientPoint.x - pendingTap.point.x, clientPoint.y - pendingTap.point.y) <= doubleTapDistance) {
        const nextZoom = pendingTap.toggleFromZoom > 1 ? 1 : 2;
        clearPendingTap();
        animateDoubleTap(clientPoint, nextZoom);
        return;
      }
      clearPendingTap();
      const tap = { point: clientPoint, time, toggleFromZoom, timer: null };
      tap.timer = window.setTimeout(() => {
        if (pendingTap !== tap) return;
        pendingTap = null;
        if (!pointers.size && !waitingForImage && !navigationQueue.length && !slide) onTap?.(tap.point);
      }, doubleTapMs);
      pendingTap = tap;
    };

    const clearSlide = () => {
      if (!slide) return;
      if (slide.timeout != null) window.clearTimeout?.(slide.timeout);
      slide.neighbor.remove();
      image.style.translate = '';
      image.style.transition = '';
      slide = null;
    };

    const prepareSlide = (direction) => {
      if (slide?.settling) return false;
      if (slide?.direction === direction) {
        if (slide.visualSrc !== slide.adjacent.src && preload(slide.adjacent.src).state === 'ready') {
          slide.neighbor.src = slide.adjacent.src;
          slide.visualSrc = slide.adjacent.src;
        }
        return true;
      }
      clearSlide();
      const adjacent = getAdjacent?.(direction);
      if (!adjacent?.src || !image.cloneNode || !viewport.appendChild) return false;
      const source = visualSource(adjacent);
      if (!source) {
        const key = `${adjacent.src}|${adjacent.previewSrc || ''}`;
        if (gesture?.type === 'swipe' && gesture.waitingSrc !== key) {
          gesture.waitingSrc = key;
          waitForVisual(adjacent).then((ready) => {
            if (ready && gesture?.type === 'swipe' && gesture.waitingSrc === key
              && prepareSlide(gesture.dx < 0 ? 1 : -1)) positionSlide(gesture.dx);
          });
        }
        return false;
      }
      const neighbor = image.cloneNode(false);
      neighbor.removeAttribute('style');
      neighbor.removeAttribute('data-theme-media-lightbox-image');
      neighbor.removeAttribute('data-theme-quick-view-lightbox-image');
      neighbor.classList.add('theme-main-product__lightbox-slide-image');
      neighbor.setAttribute('aria-hidden', 'true');
      neighbor.alt = '';
      neighbor.src = source;
      neighbor.draggable = false;
      viewport.appendChild(neighbor);
      slide = { direction, adjacent, visualSrc: source, neighbor, offset: 0, settling: false, timeout: null };
      if (source !== adjacent.src) {
        const currentSlide = slide;
        preload(adjacent.src).promise.then((ready) => {
          if (ready && slide === currentSlide && !slide.settling) {
            slide.neighbor.src = adjacent.src;
            slide.visualSrc = adjacent.src;
          }
        });
      }
      return true;
    };

    const positionSlide = (offset) => {
      if (!slide) return;
      const width = viewport.clientWidth;
      slide.offset = Math.max(-width, Math.min(width, offset));
      image.style.translate = `${slide.offset}px 0px`;
      slide.neighbor.style.translate = `${slide.offset + slide.direction * width}px 0px`;
    };

    const settleSlide = (commit) => {
      if (!slide || slide.settling) return;
      const direction = slide.direction;
      const finish = () => {
        if (!slide || slide.direction !== direction) return;
        const { adjacent, visualSrc } = slide;
        clearSlide();
        if (commit) {
          const generation = ++navigationGeneration;
          const zoomResource = visualSrc === adjacent.src ? null : preload(adjacent.src);
          onNavigate?.(direction, visualSrc);
          if (zoomResource) {
            zoomResource.promise.then((ready) => {
              if (ready && generation === navigationGeneration && !slide) onUpgrade?.(adjacent.src, visualSrc);
            });
          }
        }
        processQueue();
      };
      slide.settling = true;
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }
      const transition = 'translate 260ms cubic-bezier(0.22, 1, 0.36, 1)';
      image.style.transition = transition;
      slide.neighbor.style.transition = transition;
      positionSlide(commit ? -direction * viewport.clientWidth : 0);
      slide.timeout = window.setTimeout(finish, 280);
    };

    const processQueue = () => {
      if (waitingForImage || slide || !navigationQueue.length) return;
      const direction = navigationQueue.shift();
      const adjacent = getAdjacent?.(direction);
      if (!adjacent?.src) return processQueue();
      const generation = navigationGeneration;
      waitingForImage = true;
      waitForVisual(adjacent).then((source) => {
        if (generation !== navigationGeneration) return;
        waitingForImage = false;
        if (source) {
          if (zoom > 1) setZoom(1);
          if (prepareSlide(direction)) {
            positionSlide(0);
            void viewport.offsetWidth; // Establish the offscreen starting position.
            settleSlide(true);
            return;
          }
        }
        processQueue();
      });
    };

    const navigate = (direction) => {
      if (direction !== -1 && direction !== 1) return;
      cancelTapAnimation();
      clearPendingTap();
      navigationQueue.push(direction);
      processQueue();
    };

    const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const point = (event) => ({ x: event.clientX, y: event.clientY });
    const isImagePoint = (event) => {
      // The image has pointer-events: none, so hit testing lands on its viewport.
      // Use the displayed image bounds to keep letterbox margins and controls out.
      if (event.target !== viewport && event.target !== image) return false;
      const rect = viewport.getBoundingClientRect();
      const fitted = fittedSize();
      const x = event.clientX - rect.left - viewport.clientWidth / 2 - pan.x;
      const y = event.clientY - rect.top - viewport.clientHeight / 2 - pan.y;
      return Math.abs(x) <= fitted.width * zoom / 2 && Math.abs(y) <= fitted.height * zoom / 2;
    };

    const beginGesture = () => {
      const active = Array.from(pointers.values());
      if (active.length === 2) {
        clearPendingTap();
        clearSlide();
        gesture = {
          type: 'pinch', midpoint: midpoint(active[0], active[1]),
          distance: Math.max(1, distance(active[0], active[1])), zoom, pan: { ...pan }
        };
        suppressClick = true;
      } else if (active.length === 1) {
        gesture = { type: zoom > 1 ? 'pan' : 'swipe', start: active[0], pan: { ...pan },
          tapEligible: false };
      }
    };

    const pointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const toggleFromZoom = tapAnimation?.targetZoom ?? zoom;
      cancelTapAnimation();
      if (event.pointerType === 'touch' && pendingTap
        && (event.timeStamp - pendingTap.time > doubleTapMs
          || Math.hypot(event.clientX - pendingTap.point.x, event.clientY - pendingTap.point.y) > doubleTapDistance)) {
        clearPendingTap();
      }
      pointers.set(event.pointerId, point(event));
      if (waitingForImage || navigationQueue.length || slide?.settling) {
        gesture = pointers.size === 1 ? { type: 'queuedSwipe', start: point(event) } : null;
      } else {
        beginGesture();
      }
      if (gesture && pointers.size === 1) {
        gesture.tapEligible = gesture.type !== 'queuedSwipe'
          && event.pointerType === 'touch' && isImagePoint(event);
        gesture.tapToggleFromZoom = toggleFromZoom;
      }
      viewport.setPointerCapture?.(event.pointerId);
      if (event.cancelable && (event.pointerType !== 'mouse' || zoom > 1)) event.preventDefault();
    };

    const pointerMove = (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, point(event));
      if (gesture?.tapEligible && distance(point(event), gesture.start) > tapMovement) {
        gesture.tapEligible = false;
        clearPendingTap();
      }
      if (event.cancelable) event.preventDefault();
      if (gesture?.type === 'pinch' && pointers.size >= 2) {
        const active = Array.from(pointers.values());
        const center = midpoint(active[0], active[1]);
        const nextZoom = Math.max(1, Math.min(4, gesture.zoom * distance(active[0], active[1]) / gesture.distance));
        const rect = viewport.getBoundingClientRect();
        const viewportCenter = { x: viewport.clientWidth / 2, y: viewport.clientHeight / 2 };
        const ratio = nextZoom / gesture.zoom;
        pan = {
          x: center.x - rect.left - viewportCenter.x - (gesture.midpoint.x - rect.left - viewportCenter.x - gesture.pan.x) * ratio,
          y: center.y - rect.top - viewportCenter.y - (gesture.midpoint.y - rect.top - viewportCenter.y - gesture.pan.y) * ratio
        };
        zoom = nextZoom;
        render();
      } else if (gesture?.type === 'pan' && pointers.size === 1) {
        const current = pointers.values().next().value;
        pan = { x: gesture.pan.x + current.x - gesture.start.x, y: gesture.pan.y + current.y - gesture.start.y };
        if (Math.hypot(current.x - gesture.start.x, current.y - gesture.start.y) > 6) suppressClick = true;
        viewport.classList.add('is-dragging');
        render();
      } else if (gesture?.type === 'swipe' && pointers.size === 1) {
        const current = pointers.values().next().value;
        const dx = current.x - gesture.start.x;
        const dy = current.y - gesture.start.y;
        gesture.dx = dx;
        if (Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy) * 1.2 && prepareSlide(dx < 0 ? 1 : -1)) {
          positionSlide(dx);
          suppressClick = true;
        }
      }
    };

    const pointerEnd = (event) => {
      if (!pointers.has(event.pointerId)) return;
      const current = point(event);
      const tappedImage = event.type === 'pointerup' && event.pointerType === 'touch'
        && gesture?.tapEligible && pointers.size === 1
        && distance(current, gesture.start) <= tapMovement
        && !waitingForImage && !navigationQueue.length && !slide;
      const tapToggleFromZoom = gesture?.tapToggleFromZoom;
      if (gesture?.type === 'swipe' && pointers.size === 1) {
        const dx = current.x - gesture.start.x;
        const dy = current.y - gesture.start.y;
        const horizontal = Math.abs(dx) > Math.abs(dy) * 1.25;
        if (event.type !== 'pointercancel' && horizontal && Math.abs(dx) > 6) {
          prepareSlide(dx < 0 ? 1 : -1);
          positionSlide(dx);
        }
        if (slide) {
          const commit = event.type !== 'pointercancel' && horizontal
            && Math.abs(dx) >= Math.max(48, viewport.clientWidth * 0.18);
          settleSlide(commit);
          suppressClick = true;
        } else if (event.type !== 'pointercancel' && horizontal
          && Math.abs(dx) >= Math.max(48, viewport.clientWidth * 0.18)) {
          navigate(dx < 0 ? 1 : -1);
          suppressClick = true;
        }
      } else if (gesture?.type === 'queuedSwipe' && pointers.size === 1 && event.type !== 'pointercancel') {
        const dx = current.x - gesture.start.x;
        const dy = current.y - gesture.start.y;
        if (Math.abs(dx) >= Math.max(48, viewport.clientWidth * 0.18)
          && Math.abs(dx) > Math.abs(dy) * 1.25) {
          navigate(dx < 0 ? 1 : -1);
          suppressClick = true;
        }
      }
      pointers.delete(event.pointerId);
      viewport.releasePointerCapture?.(event.pointerId);
      viewport.classList.remove('is-dragging');
      gesture = null;
      // A remaining finger starts a fresh pan rather than accidentally swiping.
      if (pointers.size && zoom > 1) beginGesture();
      render();
      if (tappedImage) {
        const click = { point: current, timer: null };
        click.timer = window.setTimeout(() => {
          const index = touchClicks.indexOf(click);
          if (index !== -1) touchClicks.splice(index, 1);
        }, 500);
        touchClicks.push(click);
        lastTouchTapTime = event.timeStamp;
        recordTap(current, event.timeStamp, tapToggleFromZoom);
      } else if (event.pointerType === 'touch') {
        clearPendingTap();
      }
      if (suppressClick) window.setTimeout(() => { suppressClick = false; }, 0);
    };

    // iOS does not reliably dispatch dblclick for touch. Swallow the compatibility
    // click for a recognized image tap before host edge-click navigation sees it.
    const captureClick = (event) => {
      if (event.target !== image && event.target !== viewport) return;
      const index = touchClicks.findIndex((click) => distance(point(event), click.point) <= doubleTapDistance);
      if (index === -1) return;
      const [click] = touchClicks.splice(index, 1);
      window.clearTimeout?.(click.timer);
      event.preventDefault();
      event.stopPropagation();
    };

    const wheel = (event) => {
      event.preventDefault();
      setZoom(zoom + (event.deltaY < 0 ? 0.2 : -0.2));
    };
    const doubleClick = (event) => {
      if (event.pointerType === 'touch' || event.timeStamp - lastTouchTapTime < 500 || !isImagePoint(event)) return;
      event.preventDefault();
      animateDoubleTap(point(event), (tapAnimation?.targetZoom ?? zoom) > 1 ? 1 : 2);
    };

    const refresh = () => {
      cancelTapAnimation();
      render();
    };

    viewport.addEventListener('pointerdown', pointerDown);
    viewport.addEventListener('pointermove', pointerMove);
    viewport.addEventListener('pointerup', pointerEnd);
    viewport.addEventListener('pointercancel', pointerEnd);
    viewport.addEventListener('wheel', wheel, { passive: false });
    viewport.addEventListener('dblclick', doubleClick);
    viewport.addEventListener('click', captureClick, true);
    image.addEventListener('load', refresh);

    return {
      get zoom() { return zoom; },
      setZoom,
      navigate,
      prime,
      refresh,
      reset({ preserveNavigation = false } = {}) {
        pointers.clear();
        gesture = null;
        cancelTapAnimation();
        clearPendingTap();
        clearTouchClicks();
        clearSlide();
        if (!preserveNavigation) {
          navigationQueue.length = 0;
          navigationGeneration += 1;
          waitingForImage = false;
        }
        setZoom(1);
      },
      consumeClick() { const result = suppressClick; suppressClick = false; return result; },
      destroy() {
        cancelTapAnimation();
        clearPendingTap();
        clearTouchClicks();
        clearSlide();
        navigationQueue.length = 0;
        navigationGeneration += 1;
        preloads.clear();
        viewport.removeEventListener('pointerdown', pointerDown);
        viewport.removeEventListener('pointermove', pointerMove);
        viewport.removeEventListener('pointerup', pointerEnd);
        viewport.removeEventListener('pointercancel', pointerEnd);
        viewport.removeEventListener('wheel', wheel);
        viewport.removeEventListener('dblclick', doubleClick);
        viewport.removeEventListener('click', captureClick, true);
        image.removeEventListener('load', refresh);
      }
    };
  };
})();
